/**
 * Numeric guards shared by every calc function.
 *
 * Rule for this whole module (spec "Computed fields and acceptance rules"):
 * a computed value is either a finite number or `null`. It is never `NaN`,
 * never `Infinity`, and no calc function ever throws. A half-filled field on a
 * phone in the rain is the normal case, not an error case.
 */

/**
 * What a form field can actually hand us.
 *
 * `null` / `undefined` = not entered yet. Strings are tolerated because number
 * inputs surface their value as a string; an empty or non-numeric string is
 * treated as "not entered".
 */
export type Numeric = number | string | null | undefined;

/**
 * Coerce an input to a finite number, or `null`.
 *
 * `null`, `undefined`, `''`, whitespace, `NaN`, `Infinity` and anything
 * non-numeric all become `null`.
 */
export function toFinite(value: Numeric): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Divide, returning `null` rather than `NaN` or `Infinity`.
 *
 * Returns `null` when either operand is missing/non-numeric or the divisor is
 * zero. This is the single choke point for every ratio in the app.
 */
export function divide(numerator: Numeric, denominator: Numeric): number | null {
  const n = toFinite(numerator);
  const d = toFinite(denominator);
  if (n === null || d === null || d === 0) return null;
  const quotient = n / d;
  return Number.isFinite(quotient) ? quotient : null;
}

/** Arithmetic mean of the finite values, or `null` when there are none. */
export function mean(values: readonly Numeric[]): number | null {
  const finite = values.map(toFinite).filter((v): v is number => v !== null);
  if (finite.length === 0) return null;
  const total = finite.reduce((sum, v) => sum + v, 0);
  const result = total / finite.length;
  return Number.isFinite(result) ? result : null;
}

/**
 * Round half-up to `decimals` places, for display only.
 *
 * Computed values are stored and compared at full precision; rounding happens
 * at the edge so a displayed 0.076 never feeds another calculation.
 *
 * ONE documented exception: `actualWtPerCount` in ./calibration rounds to
 * CHECK_WT_PER_COUNT_DECIMALS and that rounded figure IS the judged value,
 * because the workbook rounds before comparing. It says so at length; nothing
 * else may follow its example.
 */
export function roundTo(value: Numeric, decimals: number): number | null {
  const v = toFinite(value);
  if (v === null) return null;
  const factor = 10 ** decimals;
  const scaled = v * factor;
  // Nudge off the binary boundary so e.g. 1.005 * 100 === 100.49999999999999
  // still rounds to 1.01.
  const nudged = scaled + Math.sign(scaled) * 1e-9;
  return Math.round(nudged) / factor;
}

/**
 * Truncate toward zero to `decimals` places, for display only.
 *
 * Needed because the existing workbook truncates the printed W/C ratio rather
 * than rounding it — 1990.2 / 11247 = 0.17695 prints as 0.17, not 0.18. The
 * spec requires our arithmetic to reproduce the sheet's.
 */
export function truncateTo(value: Numeric, decimals: number): number | null {
  const v = toFinite(value);
  if (v === null) return null;
  const factor = 10 ** decimals;
  const scaled = v * factor;
  // Guard the binary representation, e.g. 0.29 * 100 === 28.999999999999996.
  const nudged = scaled + Math.sign(scaled) * 1e-9;
  return Math.trunc(nudged) / factor;
}
